import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslateModule } from '@ngx-translate/core';
import { MatTooltip } from '@angular/material/tooltip';
import { MatIcon } from '@angular/material/icon';
import {
  Attachment,
  AttachmentUploadContract,
} from '@app-types/api/attachment';
import { AttachmentState } from '@app-types/enums/attachment-state';
import { BaseGetByEmailFileIdRequest } from '@app-types/base/base';
import { FileClient } from '@app-services/api/clients/file.client';
import { MatchError } from '@app-services/errors/error-matcher';
import { FormatSizePipe } from '@app-pipes/format-size.pipe';
import { NgxFileDropEntry, NgxFileDropModule } from 'ngx-file-drop';
import { MobileObserverService } from '@app-services/adaptive/mobile-observer.service';
import { Subscription } from 'rxjs';
import { MatIconButton } from '@angular/material/button';
import { EmailClient } from '@app-services/api/clients/email-client';
import { TempFileClient } from '@app-services/api/clients/temp-file.client';

@Component({
  selector: 'app-email-attachments-list',
  imports: [
    MatProgressSpinnerModule,
    TranslateModule,
    MatTooltip,
    MatIcon,
    FormatSizePipe,
    NgxFileDropModule,
    MatIconButton,
  ],
  templateUrl: './email-attachments-list.component.html',
  styleUrls: ['./email-attachments-list.component.scss'],
})
export class EmailAttachmentsListComponent implements OnInit, OnDestroy {
  @Input() attachmentFiles: Attachment[] | AttachmentUploadContract[];
  @Input() emailId?: number;
  @Input() messageSize: string;
  @Input() isDragging = false;
  @Input() isDetails = false;
  @Input() disabled = false;
  @Output() resetIsDragging = new EventEmitter();
  @Output() addAttachmentFile = new EventEmitter();
  @Output() deleteAttachment = new EventEmitter<AttachmentUploadContract>();
  @ViewChild('uploader') uploader: ElementRef;

  public attachmentState = AttachmentState;
  isLoadingDownload = false;
  public downloadingFileIds: number[] = [];

  private useTabletViewSubscription: Subscription;
  public useTabletView = false;

  constructor(
    private tempFileClient: TempFileClient,
    private fileClient: FileClient,
    public matchError: MatchError,
    private mobileObserverService: MobileObserverService,
    private emailClient: EmailClient
  ) {}

  ngOnInit(): void {
    this.useTabletViewSubscription = this.mobileObserverService
      .tabletObserver()
      .subscribe(isTablet => (this.useTabletView = isTablet));
  }

  isFileDownloading(fileId?: number): boolean {
    return !fileId ? false : this.downloadingFileIds.includes(fileId);
  }

  async getAttachmentFile(
    file: Attachment | AttachmentUploadContract,
    emailId: number
  ): Promise<void> {
    try {
      if (!file.fileId) return;
      this.downloadingFileIds.push(file.fileId);
      const request = new BaseGetByEmailFileIdRequest(file.fileId, emailId);
      await this.fileClient.download(request);
    } catch (e) {
      this.matchError.errorHandler(e);
      this.matchError.logError(e);
    } finally {
      this.downloadingFileIds = this.downloadingFileIds.filter(
        e => e !== file.fileId
      );
    }
  }

  async onDrop(event: NgxFileDropEntry[]): Promise<void> {
    this.resetIsDragging.emit();
    const filesQue: File[] = [];

    const filePromises = event.map(fileInfo => {
      return new Promise<void>((resolve, reject) => {
        const fileEntry = fileInfo.fileEntry as FileSystemFileEntry;
        fileEntry.file(async (file: File) => {
          filesQue.push(file);
          resolve();
        }, reject);
      });
    });

    await Promise.all(filePromises);
    this.addAttachmentFile.emit(filesQue);
  }

  deleteFileAttachment(file: Attachment | AttachmentUploadContract): void {
    file && this.deleteAttachment?.emit(file as AttachmentUploadContract);
  }

  async downloadAllFiles(emailId: number): Promise<void> {
    try {
      this.isLoadingDownload = true;
      const fileResponse = await this.emailClient.downloadAttachments({
        emailId,
      });
      const request = new BaseGetByEmailFileIdRequest(
        fileResponse.fileId,
        emailId
      );
      await this.tempFileClient.download(request);
    } catch (e) {
      this.matchError.errorHandler(e);
      this.matchError.logError(e);
    } finally {
      this.isLoadingDownload = false;
    }
  }

  ngOnDestroy(): void {
    this.useTabletViewSubscription?.unsubscribe();
  }

  getSize(): number {
    return this.attachmentFiles.reduce((acc, el) => {
      if (el.size) acc += el.size;
      return acc;
    }, 0);
  }

  openFileSelector(event: any): void {
    const needIds = ['attachments', 'titleAttachments', 'containerAttachments'];
    const id = event?.target?.id;
    needIds.includes(id) && (this.uploader as any)?.openFileSelector();
  }
}
